import * as React from "react";
import Steps from "antd/es/steps";
import Flex from "antd/es/flex";
import Result from "antd/es/result";
import { steps } from "./constants";
import { TransferForm } from "./TransferForm";
import { CallbackParams, FormModel } from "./types";
import { PaymentGate } from "./PaymentGate";
import { useCreateExchange } from "../api";
import { SuccessDisplay } from "./SuccessDisplay";
import { ErrorDisplay } from "./ErrorDisplay";
import { useSearchParams } from "react-router-dom";
import { Reserves } from "./Reserves";
import { TokenSelect } from "./TokenSelect";
import { ReservesDisplay } from "./ReservesDisplay";

export const Exchange: React.FunctionComponent = () => {
    const [params] = useSearchParams();
    const encoded = params.get("encoded");
    const decoded = React.useMemo<CallbackParams | undefined>(() => {
        if (typeof encoded === "string" && encoded) {
            const json = window.atob(encoded);
            params.delete("encoded");
            const model: CallbackParams = JSON.parse(json);
            return model;
        }
        return undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [encoded]);
    const [step, setStep] = React.useState<typeof steps[keyof typeof steps]>(decoded?.step || 0);
    const [formModel, setFormModel] = React.useState<FormModel | undefined>(decoded?.formModel);
    const createExchange = useCreateExchange();

    React.useEffect(() => {
        window.scrollTo(0, 0);
    }, [step]);

    return (
        <Flex vertical gap="25px">
            {step !== 4 && (
                <Steps
                    size="small"
                    current={step}
                    items={Object.keys(steps).map((step) => ({
                        title: step,
                    }))}
                />
            )}
            {step === 0 && (
                <TokenSelect
                    onSelect={(token) => {
                        setStep(1);
                        setFormModel((prevModel) => ({
                            ...prevModel!,
                            token,
                        }));
                    }}
                />
            )}
            {formModel?.token && (
                <Reserves token={formModel.token}>
                    {(reserves) => (
                        <>
                            {step === 1 && formModel && (reserves ? (
                                <TransferForm
                                    onFinish={async (values) => {
                                        setFormModel(await createExchange.mutateAsync(values));
                                        setStep(2);
                                    }}
                                    onBack={() => setStep(0)}
                                    initialValues={formModel}
                                    reserves={reserves}
                                />
                            ) : <Result title="Reserves not available" status="error" />)}
                            {step === 2 && formModel && (
                                <PaymentGate
                                    formModel={formModel}
                                    setSuccess={() => {
                                        setStep(3);
                                    }}
                                    onError={() => {
                                        setStep(4);
                                    }}
                                    setTransactionId={(txId) => setFormModel(formModel => ({
                                        ...formModel!,
                                        transactionHash: txId,
                                    }))}
                                    onBack={() => setStep(1)}
                                />
                            )}
                            {step === 3 && (
                                <SuccessDisplay />
                            )}
                            {step === 4 && formModel && (
                                <ErrorDisplay orderId={formModel.orderId} />
                            )}
                            {reserves && (
                                <ReservesDisplay reserves={reserves} token={formModel.token} />
                            )}
                        </>
                    )}
                </Reserves>
            )}
        </Flex>
    );
};